import React, { useEffect, useMemo, useRef, useState } from 'react';
import ModalPortal from '../../../components/ModalPortal';

export default function ShareFamilyModal({ isOpen, onClose, family, projects, currentProjectId, onShare }) {
    const overlayMouseDown = useRef(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) setSelectedIds([]);
    }, [isOpen, family?.id]);

    const candidates = useMemo(() => {
        if (!family) return [];
        return (projects || []).filter(project => {
            if (String(project.id) === String(currentProjectId)) return false;
            const linked = project.linkedFamilies || [];
            return !linked.some(id => String(id) === String(family.id));
        });
    }, [projects, currentProjectId, family]);

    if (!isOpen || !family) return null;

    const toggleProject = (projectId) => {
        setSelectedIds(prev => prev.includes(projectId)
            ? prev.filter(id => id !== projectId)
            : [...prev, projectId]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedIds.length === 0) return;
        setIsSubmitting(true);
        try {
            await onShare(family, selectedIds);
            setSelectedIds([]);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ModalPortal>
        <div
            className="modal-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) overlayMouseDown.current = true; }}
            onMouseUp={(e) => {
                if (e.target === e.currentTarget && overlayMouseDown.current) onClose();
                overlayMouseDown.current = false;
            }}
        >
            <div className="modal-content" onClick={e => e.stopPropagation()} style={{ minWidth: '400px' }}>
                <h2 style={{ color: 'var(--text-primary)', margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>Share family</h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                    Link <strong>{family.name}</strong> to other projects. They will use the same model until you unshare.
                </p>
                <form onSubmit={handleSubmit} className="importer-form">
                    {candidates.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '0.85rem' }}>
                            This family is already linked to every project.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '240px', overflowY: 'auto' }}>
                            {candidates.map(project => (
                                <label key={project.id} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.6rem',
                                    padding: '0.5rem 0.75rem',
                                    background: 'var(--bg-secondary)',
                                    border: '1px solid var(--border)',
                                    borderRadius: 'var(--radius)',
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(project.id)}
                                        onChange={() => toggleProject(project.id)}
                                    />
                                    <span style={{ fontWeight: 600 }}>{project.name}</span>
                                </label>
                            ))}
                        </div>
                    )}
                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                        <button
                            type="submit"
                            className="btn-primary"
                            disabled={selectedIds.length === 0 || isSubmitting}
                            style={{ background: 'var(--text-primary)', color: 'var(--bg-primary)' }}
                        >
                            {isSubmitting ? 'Sharing…' : 'Share'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
        </ModalPortal>
    );
}
